import React from "react";
import { Box, Text, Card, Stack, TextField, Inline } from "braid-design-system";
import { useTranslation } from "react-i18next";
import { searchItems } from "../../services/promotionService";
import NumericTextField from "../NumericTextField";

interface PromotionDiscountSectionProps {
    discPercent: string;
    discValue: string;
    freeItem: string;
    freeVoucher: string;
    onChange: (field: string, value: any) => void;
    isReadOnly?: boolean;
    error?: string;
}

const PromotionDiscountSection: React.FC<PromotionDiscountSectionProps> = ({
    discPercent,
    discValue,
    freeItem,
    freeVoucher,
    onChange,
    isReadOnly = false,
    error,
}) => {
    const { t } = useTranslation();
    const [freeItemSearchResults, setFreeItemSearchResults] = React.useState<{ item_code: string; item_name: string }[]>([]);

    const handleFreeItemSearch = async (value: string) => {
        onChange('promo_free_item', value);

        if (value.trim().length < 2 || isReadOnly) {
            setFreeItemSearchResults([]);
            return;
        }

        const results = await searchItems(value.trim());
        setFreeItemSearchResults(results);
    };

    const handleFreeItemSelect = (item: { item_code: string; item_name: string }) => {
        onChange('promo_free_item', item.item_code);
        setFreeItemSearchResults([]);
    };

    return (
        <Card>
            <Stack space="medium">
                <Text weight="strong" size="large">
                    {t('page.promotion.discount.title', 'Promotion')}
                </Text>

                <Inline space="small" alignY="center">
                    <NumericTextField
                        id="promo-disc-percent"
                        label={t('page.promotion.discount.percent', 'Discount %')}
                        placeholder="e.g. 10"
                        value={discPercent}
                        max={100}
                        tone={error ? "critical" : undefined}
                        message={error}
                        onChange={(value) => onChange('promo_disper', value)}
                        disabled={isReadOnly}
                    />
                    <NumericTextField
                        id="promo-disc-value"
                        label={t('page.promotion.discount.value', 'Discount Value')}
                        placeholder="e.g. 500.00"
                        value={discValue}
                        tone={error ? "critical" : undefined}
                        onChange={(value) => onChange('promo_disamt', value)}
                        disabled={isReadOnly}
                    />
                </Inline>

                <Inline space="small" alignY="center">
                    <Box flexGrow={1}>
                        <TextField
                            id="promo-free-item"
                            label={t('page.promotion.discount.free_item', 'Free Item')}
                            placeholder={t('page.promotion.discount.free_item_placeholder', 'Search & select free item code')}
                            value={freeItem}
                            tone={error ? "critical" : undefined}
                            onChange={(e) => handleFreeItemSearch(e.currentTarget.value)}
                            disabled={isReadOnly}
                        />
                        {freeItemSearchResults.length > 0 && (
                            <Box
                                background="neutralLight"
                                padding="small"
                                borderRadius="standard"
                                marginTop="xxsmall"
                            >
                                <Stack space="xxsmall">
                                    {freeItemSearchResults.map((item) => (
                                        <Box
                                            key={item.item_code}
                                            cursor="pointer"
                                            onClick={() => handleFreeItemSelect(item)}
                                            paddingY="xxsmall"
                                        >
                                            <Text size="small">{item.item_code} - {item.item_name}</Text>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Box>
                    <Box flexGrow={1}>
                        <TextField
                            id="promo-free-voucher"
                            label={t('page.promotion.discount.free_voucher', 'Free Voucher')}
                            placeholder={t('page.promotion.discount.free_voucher_placeholder', 'Voucher code / description')}
                            value={freeVoucher}
                            tone={error ? "critical" : undefined}
                            onChange={(e) => onChange('promo_free_voucher', e.currentTarget.value)}
                            disabled={isReadOnly}
                        />
                    </Box>
                </Inline>
            </Stack>
        </Card>
    );
};

export default PromotionDiscountSection;
